import React, { useEffect, useState, useRef } from 'react'
import styles from './categories.module.css'
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAllCategoriesThunk } from '../../redux/category/getAllCategorySlice';
import ProductTable from '../../components/OtherComps/ProductTable/ProductTable';
import { addToCartThunk } from '../../redux/cartSlice';
import { addToScheduleThunk } from '../../redux/ScheduleSlice';
import { setLoginpopup } from '../../redux/customerSlice';
import api from '../../utils/api';
import { stripHtmlTags } from '../../utils/utils';

const Categories = () => {
    const { categories, loading } = useSelector((state) => state.categories);
    const { isLoggedin } = useSelector((state) => state.customer);
    const [suggestedData, setSuggestedData] = useState([]);
    const [searchResult, setSearchResult] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [quantities, setQuantities] = useState({});
    const [cartLoading, setCartLoading] = useState({});
    const [scheduleLoading, setScheduleLoading] = useState({});
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const searchContainerRef = useRef(null);

    // Get search query from URL or default to empty string
    const searchQuery = searchParams.get('search') || '';
    const [search, setSearch] = useState(searchQuery);

    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [selectedCategoryName, setSelectedCategoryName] = useState('All categories');
    const [displayLimit, setDisplayLimit] = useState(50);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (searchContainerRef.current && !searchContainerRef.current.contains(event.target)) {
                setSuggestedData([]);
                setShowCategoryDropdown(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        dispatch(getAllCategoriesThunk());
    }, [dispatch]);

    // Sync search state with URL query parameter
    useEffect(() => {
        setSearch(searchQuery);
    }, [searchQuery]);

    // Fetch results when search query changes
    useEffect(() => {
        setDisplayLimit(50);
        if (searchQuery) {
            searchedResults(searchQuery);
        } else {
            setSearchResult([]);
        }
    }, [searchQuery]);

    const handleQuantityChange = (productId, value) => {
        setQuantities((prev) => ({
            ...prev,
            [productId]: value,
        }));
    };

    const handleCheckboxChange = (product, isChecked) => {
        setSelectedProducts((prevState) => {
            if (isChecked) {
                return [...prevState, product];
            } else {
                return prevState.filter((p) => p._id !== product?._id);
            }
        });
    };

    const addtoCart = async (from, productId) => {
        if (!isLoggedin) {
            return dispatch(setLoginpopup(true));
        }
        const cartProducts = Object.entries(quantities).map(([id, quantity]) => ({
            productId: id,
            totalQty: parseInt(quantity, 10),
            quantity: parseInt(quantity, 10),
        }));
        if (from === 'cart') {
            setCartLoading((prev) => ({ ...prev, [productId]: true }));
            const status = await dispatch(addToCartThunk(cartProducts));
            if (status?.payload === 200) {
                setQuantities({});
            }
            setCartLoading((prev) => ({ ...prev, [productId]: false }));
        } else {
            setScheduleLoading((prev) => ({ ...prev, [productId]: true }));
            const status = await dispatch(addToScheduleThunk(cartProducts));
            if (status?.payload === 200) {
                setQuantities({});
            }
            setScheduleLoading((prev) => ({ ...prev, [productId]: false }));
        }
    };

    const latestQueryRef = useRef('');
    const debounceTimerRef = useRef(null);

    const smartSearch = (text) => {
        setSearch(text);
        latestQueryRef.current = text;
        if (!text.trim()) {
            if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
            setSuggestedData([]);
            return;
        }
        if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = setTimeout(async () => {
            const currentQuery = text.trim();
            try {
                const response = await api.smartSearch(`?query=${encodeURIComponent(currentQuery)}&limit=15`);
                if (response.data && latestQueryRef.current.trim() === currentQuery) {
                    const results = response.data?.data?.results || [];
                    setSuggestedData(results.filter(i => i?.type === "product"));
                }
            } catch (err) {
                console.log(err);
            }
        }, 180);
    };

    const searchedResults = async (text) => {
        setSearchLoading(true);
        try {
            const response = await api.smartSearch(`?query=${encodeURIComponent(text)}`);
            if (response.data) {
                const results = response.data?.data?.results || [];
                setSearchResult(results.filter(i => i?.type === "product"));
            }
        } catch (err) {
            console.log(err);
        } finally {
            setSearchLoading(false);
            setSuggestedData([]);
        }
    };

    const handleSearchSubmit = () => {
        if (search.trim()) {
            searchParams.set('search', search);
        } else {
            searchParams.delete('search');
        }
        setSearchParams(searchParams, { replace: true });
    };

    const getCleanUrl = (url) => {
        if (!url) return '';
        if (url.includes('asset.electrowiz.in')) {
            return url.replace('https://asset.electrowiz.in', 'https://electrowiz-ap.s3.ap-south-1.amazonaws.com');
        }
        return url;
    };

    return (
        <>
            {loading && !categories?.length && !searchQuery ? <div style={{ display: 'flex', justifyContent: 'center', alignContent: 'center', padding: '60px 0', fontSize: '15px', color: '#64748b' }}>Please wait...</div> :
                <>
                    <div className={styles.searchHeroBanner}>
                        <span className={styles.bannerTag}>
                            {searchQuery ? 'PRODUCT SEARCH' : 'COMPONENT CATALOG'}
                        </span>
                        <h1 className={styles.bannerTitle}>
                            {searchQuery ? (
                                <>Search Results for <span className={styles.queryHighlight}>"{searchQuery}"</span></>
                            ) : (
                                'Electronic Component Categories'
                            )}
                        </h1>
                    </div>
                    <div className={styles.stickySearchSection}>
                        <form ref={searchContainerRef} className={styles.searchContainer} onSubmit={(e) => {
                            e.preventDefault();
                            handleSearchSubmit();
                        }}>
                                <div style={{ position: 'relative', width: '28%', display: 'flex', alignItems: 'center', height: '100%' }}>
                                    <div 
                                        className={styles.allCatDropdownBtn} 
                                        onClick={() => setShowCategoryDropdown(prev => !prev)}
                                    >
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{selectedCategoryName}</span>
                                        <span style={{ fontSize: '10px', marginLeft: '6px', transition: 'transform 0.2s', transform: showCategoryDropdown ? 'rotate(180deg)' : 'rotate(0deg)' }}>▼</span>
                                    </div>
                                    {showCategoryDropdown && (
                                        <div className={styles.allCatDropdownMenu}>
                                            <div 
                                                className={styles.allCatDropdownItem}
                                                onClick={() => {
                                                    setSelectedCategoryName('All categories');
                                                    setShowCategoryDropdown(false);
                                                    searchParams.delete('search');
                                                    setSearchParams(searchParams, { replace: true });
                                                }}
                                            >
                                                All categories
                                            </div>
                                            {categories?.map((cat) => (
                                                <div 
                                                    key={cat._id}
                                                    className={styles.allCatDropdownItem}
                                                    onClick={() => {
                                                        setSelectedCategoryName(cat.name);
                                                        setShowCategoryDropdown(false);
                                                        navigate(`/subcategories/${cat._id}`);
                                                    }}
                                                >
                                                    {cat.name}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div className={styles.searchContainermain}>
                                    <div>
                                        <input
                                            type="text"
                                            placeholder="Search here....."
                                            className={styles.searchInput}
                                            value={search}
                                            onChange={(e) => smartSearch(e.target.value)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleSearchSubmit();
                                                }
                                            }}
                                        />
                                        {suggestedData.length > 0 &&
                                            <div className={styles.searchBox} style={{ background: 'white', display: 'flex', justifyContent: 'flex-start' }}>
                                                <ul>
                                                    {suggestedData.map((i, idx) => (
                                                        <li key={idx} onClick={() => { setSuggestedData([]); navigate(`/ProductCode/${i?.name || i?._id}`); }} className={styles.catSearchItem}>
                                                             {i?.image ? (
                                                                <img src={getCleanUrl(i.image)} alt={i.name} className={styles.catSuggestionImage} />
                                                            ) : (
                                                                <span className={styles.catSuggestionIcon}>⚡</span>
                                                            )}
                                                            <div className={styles.catSuggestionMeta}>
                                                                <span className={styles.catSuggestionTitle}>
                                                                    Part: {i?.name}{(i?.manufacturer?.[0]?.name || i?.productOverview?.manufacturer?.[0]?.name) ? ` | ${i?.manufacturer?.[0]?.name || i?.productOverview?.manufacturer?.[0]?.name}` : ''}
                                                                </span>
                                                                {(i?.description || i?.productOverview?.description) && (
                                                                    <span className={styles.catSuggestionDescription}>
                                                                        {stripHtmlTags(i?.description || i?.productOverview?.description)}
                                                                    </span>
                                                                )}
                                                            </div>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                        }
                                    </div>
                                    <button className={styles.searchBtn} onClick={handleSearchSubmit}>
                                        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.33488 11.0342C4.23694 11.68 5.29827 12.0252 6.38462 12.0252C7.10587 12.0252 7.81987 11.873 8.48579 11.5774C9.15169 11.2819 9.75628 10.849 10.2652 10.3037C10.7742 9.75837 11.1775 9.11138 11.4526 8.39981C11.7277 7.68824 11.8692 6.92579 11.8692 6.15596C11.8692 4.99661 11.5484 3.86278 10.9466 2.89782C10.3448 1.93281 9.48872 1.17957 8.48579 0.734475C7.48276 0.289331 6.37861 0.17273 5.31329 0.399772C4.24804 0.626799 3.27055 1.18697 2.50402 2.00826C1.73755 2.82947 1.21627 3.87496 1.00515 5.01218C0.794022 6.14939 0.902328 7.3282 1.31661 8.39981C1.73091 9.47145 2.43291 10.3885 3.33488 11.0342ZM4.04961 2.39971C4.74144 1.90443 5.55402 1.64057 6.38462 1.64057C7.49826 1.64057 8.5678 2.11449 9.35752 2.96062C10.1474 3.80697 10.5923 4.95626 10.5923 6.15596C10.5923 7.05049 10.3447 7.92443 9.88149 8.66721C9.4183 9.40994 8.76066 9.98773 7.99254 10.3286C7.22453 10.6695 6.37988 10.7585 5.56508 10.5849C4.75021 10.4112 4.00069 9.98233 3.41173 9.3513C2.8227 8.7202 2.42087 7.91532 2.25802 7.03812C2.09516 6.16091 2.1788 5.25172 2.4981 4.42578C2.81739 3.59988 3.35768 2.89507 4.04961 2.39971Z" fill="white" stroke="#512DA8" strokeWidth="0.2" />
                                            <path d="M14.4588 15.4864C14.5436 15.4869 14.6274 15.4694 14.7054 15.4352C14.7836 15.401 14.8543 15.3507 14.9136 15.2877C14.973 15.2245 15.0198 15.1497 15.0517 15.0677C15.0835 14.9858 15.0999 14.8981 15.0999 14.8097C15.0999 14.7213 15.0835 14.6337 15.0517 14.5517C15.0199 14.4698 14.9731 14.3951 14.9137 14.3319C14.9137 14.3319 14.9136 14.3318 14.9136 14.3318L10.6104 9.71858C10.5513 9.65523 10.4808 9.60457 10.4026 9.56987C10.3245 9.53516 10.2404 9.51719 10.1554 9.51719C10.0703 9.51719 9.98625 9.53516 9.90808 9.56987C9.82994 9.60457 9.7594 9.65523 9.70031 9.71858C9.64123 9.78191 9.5947 9.85673 9.56307 9.9386C9.53144 10.0205 9.51523 10.108 9.51523 10.1962C9.51523 10.2845 9.53144 10.372 9.56307 10.4539C9.5947 10.5357 9.64123 10.6106 9.70031 10.6739L14.0037 15.2874L14.004 15.2877C14.0633 15.3507 14.134 15.401 14.2122 15.4352C14.2902 15.4694 14.3741 15.4869 14.4588 15.4864ZM14.4588 15.4864C14.4586 15.4864 14.4584 15.4864 14.4582 15.4864L14.4588 15.3864L14.4594 15.4864C14.4592 15.4864 14.459 15.4864 14.4588 15.4864Z" fill="white" stroke="#512DA8" strokeWidth="0.2" />
                                        </svg>
                                        Search
                                    </button>
                                </div>
                            </form>
                        </div>
                        <div className={styles.catContainer}>
                        {searchQuery ? (
                            searchLoading ? (
                                <div className={styles.browseRow3} style={{ textAlign: 'center', padding: '60px 20px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '14px' }}>
                                    <div className={styles.searchSpinner}></div>
                                    <p style={{ color: '#64748b', fontSize: '14px', margin: 0, fontWeight: '500' }}>Loading products for "{searchQuery}"...</p>
                                </div>
                            ) : searchResult?.length > 0 ? (
                                <div className={styles.browseRow3}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', margin: 0 }}>
                                            Part List for "{searchQuery}"
                                        </h3>
                                        <span style={{ fontSize: '13px', color: '#64748b' }}>
                                            {searchResult.length} part{searchResult.length > 1 ? 's' : ''} found
                                        </span>
                                    </div>
                                    <ProductTable
                                        products={searchResult.slice(0, displayLimit)}
                                        quantities={quantities}
                                        handleQuantityChange={handleQuantityChange}
                                        handleCheckboxChange={handleCheckboxChange}
                                        addtoCart={addtoCart}
                                        cartLoading={cartLoading}
                                        scheduleLoading={scheduleLoading}
                                        selectedProducts={selectedProducts}
                                        setSelectedProducts={setSelectedProducts}
                                    />
                                    {searchResult.length > displayLimit && (
                                        <div style={{ textAlign: 'center', padding: '24px 0 10px 0' }}>
                                            <button
                                                type="button"
                                                onClick={() => setDisplayLimit(prev => prev + 50)}
                                                style={{
                                                    backgroundColor: '#512da8',
                                                    color: '#ffffff',
                                                    border: 'none',
                                                    padding: '10px 26px',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    boxShadow: '0 2px 4px rgba(81, 45, 168, 0.2)',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#4527a0'}
                                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#512da8'}
                                            >
                                                Load More Parts (Showing {Math.min(displayLimit, searchResult.length)} of {searchResult.length})
                                            </button>
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <div className={styles.browseRow3} style={{ textAlign: 'center', padding: '40px 20px' }}>
                                    <h3 style={{ color: '#64748b', fontWeight: '500', margin: '0 0 8px 0' }}>No part results found for "{searchQuery}"</h3>
                                    <p style={{ color: '#94a3b8', fontSize: '14px', margin: 0 }}>Try searching with a different part number or keyword.</p>
                                </div>
                            )
                        ) : (
                            <div className={styles.browseRow2}>
                                <div className={styles.regRow}>
                                    {categories?.map((category) => {
                                        return (
                                            <div key={category?._id} className={styles.regCol} onClick={() => navigate(`/subcategories/${category?._id}`)} style={{ cursor: 'pointer' }}>
                                                <img src={category?.thumbnail?.photo[0]} className={styles.categoryImage} style={{ width: 80, height: 80, objectFit: 'cover' }} alt={category?.name || ''} />
                                                <span>{category?.name}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                </>
            }
        </>
    );
};

export default Categories;
